import { useState, useEffect } from 'react'
import { XMarkIcon, CameraIcon } from '@heroicons/react/24/outline'
import html2canvas from 'html2canvas'
import { supabase } from '@/lib/supabase'
import { apiClient } from '@/lib/api/base'
import { IssueCreate, IssueResponse } from '@/types/issue'

interface IssueReportModalProps {
  isOpen: boolean
  onClose: () => void
}

export default function IssueReportModal({ isOpen, onClose }: IssueReportModalProps) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [screenshot, setScreenshot] = useState<string | null>(null)
  const [isCapturing, setIsCapturing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Capture screenshot when modal opens
  useEffect(() => {
    if (isOpen && !screenshot) {
      captureScreenshot()
    }
  }, [isOpen])

  const captureScreenshot = async () => {
    setIsCapturing(true)
    setError(null)

    try {
      // Hide the modal temporarily
      const modalElement = document.querySelector('[role="dialog"]') as HTMLElement
      if (modalElement) {
        modalElement.style.display = 'none'
      }

      // Wait a bit for the modal to hide
      await new Promise((resolve) => setTimeout(resolve, 100))

      // Capture the screenshot with high quality
      // Use scale 2 for retina displays, fallback to devicePixelRatio or 1
      const deviceScale = window.devicePixelRatio || 1
      const scale = Math.min(deviceScale, 2) // Cap at 2x to avoid huge files
      
      const canvas = await html2canvas(document.body, {
        allowTaint: true,
        useCORS: true,
        logging: false,
        scale: scale, // High resolution for crisp screenshots
        backgroundColor: '#ffffff',
        removeContainer: false,
      })

      // Show the modal again
      if (modalElement) {
        modalElement.style.display = ''
      }

      // Convert to data URL with high quality JPEG
      // Use 0.95 quality for excellent image quality while keeping reasonable file size
      const screenshotDataUrl = canvas.toDataURL('image/jpeg', 0.95)
      setScreenshot(screenshotDataUrl)
    } catch (err) {
      console.error('Failed to capture screenshot:', err)
      setError('Failed to capture screenshot. You can still submit without one.')
      
      // Show the modal again in case of error
      const modalElement = document.querySelector('[role="dialog"]') as HTMLElement
      if (modalElement) {
        modalElement.style.display = ''
      }
    } finally {
      setIsCapturing(false)
    }
  }

  const uploadScreenshot = async (dataUrl: string): Promise<string | null> => {
    if (!supabase) {
      console.warn('Supabase not configured, skipping screenshot upload')
      return null
    }

    try {
      // Convert data URL to blob
      const response = await fetch(dataUrl)
      const blob = await response.blob()

      // Generate unique filename
      const filename = `issue-${Date.now()}-${Math.random().toString(36).substring(7)}.jpg`

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('issue-screenshots')
        .upload(filename, blob, {
          contentType: 'image/jpeg',
          cacheControl: '3600',
        })

      if (error) {
        console.error('Failed to upload screenshot:', error)
        return null
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('issue-screenshots')
        .getPublicUrl(filename)

      return urlData.publicUrl
    } catch (err) {
      console.error('Error uploading screenshot:', err)
      return null
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!title.trim() || !description.trim()) {
      setError('Please fill in all required fields')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      // Upload screenshot if available
      let screenshotUrl: string | null = null
      if (screenshot) {
        screenshotUrl = await uploadScreenshot(screenshot)
      }

      // Extract project_id from URL if on a project page
      // URL pattern: /project/{projectId}/... or /projects/{projectId}/...
      const urlMatch = window.location.pathname.match(/\/projects?\/([a-f0-9-]+)/i)
      const projectId = urlMatch ? urlMatch[1] : null

      // Prepare issue data
      const issueData: IssueCreate = {
        title: title.trim(),
        description: description.trim(),
        screenshot_url: screenshotUrl,
        user_agent: navigator.userAgent,
        page_url: window.location.href,
        project_id: projectId,
      }

      console.log('📝 Submitting issue with project_id:', projectId)

      // Submit issue to backend
      const response = await apiClient.post<IssueResponse>('/api/issues', issueData)

      if (response.success) {
        setSuccess(true)
        setTimeout(() => {
          handleClose()
        }, 2000)
      } else {
        setError('Failed to submit issue. Please try again.')
      }
    } catch (err: any) {
      console.error('Failed to submit issue:', err)
      setError(err.message || 'Failed to submit issue. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setTitle('')
    setDescription('')
    setScreenshot(null)
    setError(null)
    setSuccess(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[70] overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="issue-modal-title"
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        />

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-5 border-b border-gray-200">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900" id="issue-modal-title">
                  Report an Issue
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Help us improve by describing the problem you encountered
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors rounded p-1 hover:bg-gray-100"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="bg-white px-6 py-6">
            {/* Success Message */}
            {success && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800 font-medium">
                  ✓ Issue reported successfully! Thank you for your feedback.
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Title Input */}
            <div className="mb-4">
              <label htmlFor="issue-title" className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                id="issue-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Brief description of the issue"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                disabled={isSubmitting || success}
              />
            </div>

            {/* Description Textarea */}
            <div className="mb-4">
              <label htmlFor="issue-description" className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="issue-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Please provide as much detail as possible about what happened, what you expected, and any steps to reproduce the issue..."
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                required
                disabled={isSubmitting || success}
              />
            </div>

            {/* Screenshot Preview */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Screenshot
              </label>
              {isCapturing ? (
                <div className="flex items-center justify-center w-[150px] h-[200px] bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <CameraIcon className="w-6 h-6 text-gray-400 mx-auto mb-1 animate-pulse" />
                    <p className="text-xs text-gray-500">Capturing...</p>
                  </div>
                </div>
              ) : screenshot ? (
                <div className="relative inline-block">
                  <img
                    src={screenshot}
                    alt="Screenshot preview"
                    className="w-[200px] h-[140px] object-cover rounded-lg border border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={captureScreenshot}
                    className="absolute top-1 right-1 px-2 py-1 text-xs bg-white rounded shadow-md hover:bg-gray-50 transition-colors border border-gray-200"
                    disabled={isSubmitting || success}
                  >
                    Retake
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center w-[150px] h-[200px] bg-gray-100 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-center px-2">
                    <CameraIcon className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                    <p className="text-xs text-gray-500 mb-1">No screenshot</p>
                    <button
                      type="button"
                      onClick={captureScreenshot}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      disabled={isSubmitting || success}
                    >
                      Capture
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                disabled={isSubmitting || success}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Issue'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

